/**
 * The window colour of the JRPG field skin (design-mockups/jp-tasks-b.html, B5).
 *
 * One device-local setting, picked in More → Appearance. It is written to
 * `<html data-window-color>` and `jrpg.css` turns that into the window
 * gradient, so a change repaints without a render. Crimson and Sakura from
 * the mockup are left out on purpose (Pipe, 2026-10-30).
 */

export const WINDOW_COLORS = [
  { key: "royal", label: "Royal Blue", swatch: ["#2a4ad8", "#0b1460"] },
  { key: "sky", label: "Sky Cyan", swatch: ["#2aa8d8", "#08346a"] },
  { key: "teal", label: "Teal Sea", swatch: ["#1f8a8c", "#082c3a"] },
  { key: "forest", label: "Forest", swatch: ["#348c3e", "#0a2e12"] },
  { key: "purple", label: "Royal Purple", swatch: ["#7040d8", "#1e0c56"] },
  { key: "amber", label: "Amber Sunset", swatch: ["#d8822a", "#502208"] },
  { key: "steel", label: "Steel Grey", swatch: ["#606a80", "#161a24"] },
  { key: "midnight", label: "Midnight", swatch: ["#242430", "#000000"] },
] as const;

export type WindowColor = (typeof WINDOW_COLORS)[number]["key"];

export const DEFAULT_WINDOW_COLOR: WindowColor = "royal";
export const WINDOW_COLOR_STORAGE_KEY = "busee.window-color";
export const WINDOW_COLOR_EVENT = "busee:window-color-change";

export function isWindowColor(value: unknown): value is WindowColor {
  return WINDOW_COLORS.some((color) => color.key === value);
}

/** The stored colour, or the default for a missing, stale or unreadable value. */
export function readWindowColor(storage: Pick<Storage, "getItem"> | null): WindowColor {
  try {
    const value = storage?.getItem(WINDOW_COLOR_STORAGE_KEY);
    return isWindowColor(value) ? value : DEFAULT_WINDOW_COLOR;
  } catch {
    return DEFAULT_WINDOW_COLOR;
  }
}

export function applyWindowColor(color: WindowColor, root: HTMLElement = document.documentElement): void {
  root.dataset.windowColor = color;
}

/** Saves and applies the colour; `false` when this device would not store it. */
export function saveWindowColor(color: WindowColor): boolean {
  applyWindowColor(color);
  try {
    window.localStorage.setItem(WINDOW_COLOR_STORAGE_KEY, color);
  } catch {
    return false;
  }
  window.dispatchEvent(new CustomEvent(WINDOW_COLOR_EVENT));
  return true;
}
